import React from 'react';
import { only, up } from 'styled-breakpoints';
import styled from 'styled-components';

import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';

import LoadingSpinner from './LoadingSpinner';

type ButtonProps = {
  title: string;
  scheme: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

const Button = ({
  title,
  scheme,
  onClick,
  className,
  disabled = false,
  loading = false,
  ...otherProps
}: ButtonProps) => (
  <ButtonWrapper
    className={className}
    scheme={scheme}
    onClick={onClick}
    disabled={disabled || loading}
  >
    <button disabled={disabled || loading} {...otherProps}>
      {loading ? <LoadingSpinner /> : null}
      {title}
    </button>
  </ButtonWrapper>
);

export const ButtonWrapper = styled.div<{ scheme: string; disabled: boolean }>`
  button {
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${(props) =>
      colorSchemeConfig[props.scheme]
        ? colorSchemeConfig[props.scheme].v700
        : ''};
    transition: 0.1s ease;
    color: ${(props) => props.theme.colors.white};
    padding: 13px 0;
    border-radius: 6px;
    border: none;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    font-size: 16px;
    line-height: 24px;
    opacity: ${(props) => (props.disabled ? '0.5' : '1')};

    &:hover:not([disabled]) {
      opacity: 0.9;
    }

    &:active:not([disabled]) {
      opacity: 0.7;
    }
  }

  ${LoadingSpinner} {
    margin-right: 12px;
  }
`;

export const SecondaryButton = styled.button<{ scheme: string }>`
  background-color: ${(props) => colorSchemeConfig[props.scheme].v100};
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 6px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  color: ${(props) => colorSchemeConfig[props.scheme].v700};
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  transition: 0.1s ease;
  padding: 13px 0;

  &:hover,
  :active {
    background-color: ${(props) => colorSchemeConfig[props.scheme].v200};
  }
`;

export const WhiteButton = styled.button`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 6px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  color: ${(props) => props.theme.colors.grayDark3};
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  border: 1px solid ${(props) => props.theme.colors.grayDark2};

  ${only('xs')} {
    display: flex;
    justify-content: center;
  }

  ${up('sm')} {
    text-align: center;
  }

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.7;
  }
`;

export const Link = styled.a`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  width: auto;
  display: flex;
  align-items: center;
`;

export default Button;
